"use client";

import { useRef, useState, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import Image from "next/image";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import styles from "../.././app/globals.css";

// Earth shader material
const earthVertexShader = `
  varying vec2 vUv;
  varying vec3 vNormal;
  void main() {
    vUv = uv;
    vNormal = normalize(normalMatrix * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const earthFragmentShader = `
  uniform sampler2D map;
  uniform vec3 lightDirection;
  varying vec2 vUv;
  varying vec3 vNormal;
  void main() {
    vec4 texColor = texture2D(map, vUv);
    vec3 norm = normalize(vNormal);
    float lightIntensity = max(0.0, dot(norm, lightDirection)) * 1.0 + 0.5;
    vec3 sunlight = vec3(1.0, 0.95, 0.9) * lightIntensity;
    vec3 finalColor = texColor.rgb * sunlight;
    gl_FragColor = vec4(finalColor, 1.0);
  }
`;

// Glow shader material
const glowVertexShader = `
  varying vec3 vNormal;
  varying vec3 vPosition;
  void main() {
    vNormal = normalize(normalMatrix * normal);
    vPosition = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const glowFragmentShader = `
  uniform vec3 glowColor;
  uniform float glowIntensity;
  uniform float time;
  varying vec3 vNormal;
  varying vec3 vPosition;
  void main() {
    vec3 viewDir = normalize(vPosition);
    float fresnel = pow(1.0 - abs(dot(vNormal, viewDir)), 3.0);
    float intensity = glowIntensity * fresnel * (0.7 + 0.3 * sin(time * 0.5));
    vec3 glow = glowColor * intensity;
    gl_FragColor = vec4(glow, fresnel * 0.8);
  }
`;

export default function Home() {
  const [introComplete, setIntroComplete] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [texture, setTexture] = useState(null);
  const [isTextureLoading, setIsTextureLoading] = useState(true);
  const [displayText, setDisplayText] = useState("the Future"); // State for rotating text
  const contentControls = useAnimation();
  const earthControls = useAnimation();
  const earthContainerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const earthRef = useRef(null);
  const glowRef = useRef(null);
  const glowMaterialRef = useRef(null);
  const animationFrameIdRef = useRef(null);

  const primaryTexturePath = "/Earth_imgFinal0.jpg";
  const fallbackTexturePath = "/assets/Earth_imgFinal0.jpg";

  // List of phrases to rotate through
  const phrases = [
    "Bring Ideas We Make It Real",
    "The Future",
    "Intelligent Ecosystems",
    "Tech-Led Growth",
    "Data-Driven Cultures",
    "Digitally Native Models",
    "Autonomous Workflows",
    "Predictive Capabilities",
    "Cloud-First Mindsets",
    "AI-Augmented Reality",
    "Cyber-Physical Systems",
    "Platform-Centric Economies",
    "Hyperautomation Journeys",
    "Sustainable Innovation",
    "Zero-Touch Operations",
    "Machine-Led Productivity",
    "Smart Factory Floors",
    "Next-Gen Customer Experiences",
    "Real-Time Decision Engines",
    "Algorithmic Efficiency",
    "Human-Tech Synergy"
  ];

  // Handle text rotation through the phrases array
  useEffect(() => {
    let currentIndex = 0;

    const rotateText = () => {
      setDisplayText(phrases[currentIndex]);
      currentIndex = (currentIndex + 1) % phrases.length; // Loop back to start
    };

    // Initial display
    rotateText();

    // Set interval to rotate every 2 seconds
    const interval = setInterval(rotateText, 2000);

    return () => clearInterval(interval); // Cleanup on unmount
  }, []);

  // Handle mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    if (typeof window !== "undefined") {
      window.addEventListener("resize", checkMobile);
      return () => window.removeEventListener("resize", checkMobile);
    }
  }, []);

  // Handle intro animation
  useEffect(() => {
    if (introComplete) {
      contentControls.start({
        opacity: 1,
        x: 0,
        transition: { type: "spring", stiffness: 100, damping: 20 },
      });
    }
  }, [introComplete, contentControls]);

  // Handle scroll animation
  useEffect(() => {
    const handleScroll = () => {
      if (!hasScrolled && introComplete && !isMobile) {
        earthControls.start({
          x: 800,
          transition: { type: "spring", stiffness: 60, damping: 25, mass: 1.5 },
        }).then(() => {
          earthControls.start({
            x: 0,
            transition: { type: "spring", stiffness: 80, damping: 15, mass: 1, delay: 0.5 },
          }).then(() => {
            contentControls.start({
              x: 0,
              transition: { type: "spring", stiffness: 100, damping: 20, delay: 0.5 },
            });
          });
        });
        contentControls.start({
          x: -700,
          transition: { type: "spring", stiffness: 80, damping: 15, mass: 1 },
        });
        setHasScrolled(true);
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [hasScrolled, introComplete, earthControls, contentControls, isMobile]);

  // Function to set up the Earth scene without R3F
  const setupEarthScene = (container, isMobile, onTextureLoaded) => {
    // Initialize Scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Initialize Camera
    const camera = new THREE.PerspectiveCamera(
      isMobile ? 60 : 50,
      container.clientWidth / container.clientHeight,
      0.1,
      100
    );
    camera.position.set(isMobile ? 0 : 1, isMobile ? 1 : 2, isMobile ? 3 : 4);
    cameraRef.current = camera;

    // Initialize Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Check WebGL context
    if (!renderer.getContext()) {
      console.error("WebGL context not available");
    } else {
      console.log("WebGL context initialized successfully");
    }

    // Load Texture for Earth Shader using THREE.TextureLoader
    const textureLoader = new THREE.TextureLoader();
    setIsTextureLoading(true);
    textureLoader.load(
      primaryTexturePath,
      (tex) => {
        console.log("✅ Primary texture loaded");
        onTextureLoaded(tex);
        setIsTextureLoading(false);
      },
      undefined,
      (err) => {
        console.warn("⚠️ Primary texture failed, attempting fallback");
        textureLoader.load(
          fallbackTexturePath,
          (tex) => {
            console.log("✅ Fallback texture loaded");
            onTextureLoaded(tex);
            setIsTextureLoading(false);
          },
          undefined,
          (err) => {
            console.error("❌ Both primary and fallback textures failed to load", err);
            setIsTextureLoading(false);
          }
        );
      }
    );

    // Earth Shader Material
    const earthMaterial = new THREE.ShaderMaterial({
      uniforms: {
        map: { value: null },
        lightDirection: { value: new THREE.Vector3(1, 1.5, 0).normalize() },
      },
      vertexShader: earthVertexShader,
      fragmentShader: earthFragmentShader,
    });

    // Earth Mesh with Shader Material
    const earthGeometry = new THREE.SphereGeometry(1, 64, 64);
    const earthMesh = new THREE.Mesh(earthGeometry, earthMaterial);
    earthMesh.scale.set(isMobile ? 1.2 : 1.5, isMobile ? 1.2 : 1.5, isMobile ? 1.2 : 1.5);
    scene.add(earthMesh);
    earthRef.current = earthMesh;

    // Glow Shader Material
    const glowMaterial = new THREE.ShaderMaterial({
      uniforms: {
        glowColor: { value: new THREE.Color(0x66ccff) },
        glowIntensity: { value: 0.8 },
        time: { value: 0 },
      },
      vertexShader: glowVertexShader,
      fragmentShader: glowFragmentShader,
      transparent: true,
      side: THREE.FrontSide,
    });
    glowMaterialRef.current = glowMaterial;

    // Glow Mesh
    const glowMesh = new THREE.Mesh(earthGeometry, glowMaterial);
    glowMesh.scale.set(isMobile ? 1.26 : 1.575, isMobile ? 1.26 : 1.575, isMobile ? 1.26 : 1.575);
    scene.add(glowMesh);
    glowRef.current = glowMesh;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.5);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xfff8e1, 2.5);
    directionalLight.position.set(10, 5, 0);
    scene.add(directionalLight);

    // Orbit Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableZoom = !isMobile;
    controls.enablePan = false;
    controls.enableRotate = true;
    controls.minDistance = isMobile ? 3 : 4;
    controls.maxDistance = isMobile ? 6 : 8;
    controls.minPolarAngle = Math.PI / 4;
    controls.maxPolarAngle = 3 * Math.PI / 4;
    controlsRef.current = controls;

    // Animation Loop (replacing useFrame)
    const clock = new THREE.Clock();
    const animate = () => {
      const delta = clock.getDelta();

      if (earthRef.current) {
        earthRef.current.rotation.y += delta * 0.15;
      }
      if (glowRef.current) {
        glowRef.current.rotation.y += delta * 0.15;
        if (glowMaterialRef.current) {
          glowMaterialRef.current.uniforms.time.value += delta;
        }
      }

      controls.update();
      renderer.render(scene, camera);
      animationFrameIdRef.current = requestAnimationFrame(animate);
    };
    animate();

    // Debounced Resize Handler
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const width = container.clientWidth;
        const height = container.clientHeight;
        renderer.setSize(width, height);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
      }, 100); // Debounce delay of 100ms
    };

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
    }

    // Cleanup function
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", handleResize);
      }
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
      if (container && renderer.domElement) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) obj.material.dispose();
      });
    };
  };

  // Set up the Earth scene
  useEffect(() => {
    if (!earthContainerRef.current) return;

    const cleanup = setupEarthScene(earthContainerRef.current, isMobile, (tex) => {
      setTexture(tex);
    });

    // Trigger Intro Complete
    setTimeout(() => setIntroComplete(true), 500);

    return cleanup;
  }, [isMobile]);

  // Update Earth Shader Material with Texture
  useEffect(() => {
    if (texture && earthRef.current) {
      earthRef.current.material.uniforms.map.value = texture;
      earthRef.current.material.needsUpdate = true;
    }
  }, [texture]);

  // Debug hover on "earth"
  const handleEarthHover = () => {
    console.log("Hovered over 'earth' text");
  };

  // Animation variants for the vibrant text effect
  const textVariants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.8,
      color: "#93c5fd", // Starting color (matches text-blue-300)
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      color: "#60a5fa", // Vibrant blue (darker shade for pop)
      transition: {
        opacity: { duration: 0.3 },
        y: { type: "spring", stiffness: 300, damping: 20 },
        scale: { type: "spring", stiffness: 300, damping: 20 },
        color: { duration: 0.5 },
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      scale: 0.8,
      color: "#93c5fd",
      transition: { duration: 0.3, },
    },
  };

  return (
    <section className="relative flex flex-col md:flex-row items-center justify-between min-h-[80vh] md:min-h-screen bg-gray-900 text-white">
      <Image
        src="/galaxy-night-viewedt2.jpg"
        alt="company logo"
        fill
        className="object-cover absolute top-0 left-0 z-0"
        quality={100}
        priority={true}
      />
      <motion.div
        className="w-full md:w-1/2 h-[38vh] md:h-screen mt-2 md:mt-12 relative z-10 p-1 md:p-4"
        initial={{ x: 0 }}
        animate={earthControls}
      >
        <div
          ref={earthContainerRef}
          className="w-full h-[38vh] md:h-screen hover:cursor-pointer relative"
        >
          {isTextureLoading && (
            <div className="absolute inset-0 bg-gray-900 animate-pulse rounded-md" />
          )}
        </div>
      </motion.div>
      <motion.div
        className={`w-full md:w-1/2 py-2 md:p-10 relative z-20 flex flex-col items-start text-left px-2 md:px-0 ${styles.contentContainer}`}
        initial={{ opacity: 0, x: 50 }}
        animate={contentControls}
      >
        <h3 className="text-2xl md:text-6xl text-blue-300 font-bold pr-2 mt-4 md:mt-10 leading-tight">
          Guiding Businesses Into: <br />
          <motion.span 
            key={displayText}
            variants={textVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="inline-block md:text-5xl text-xl"
          >
            {displayText}
          </motion.span>
        </h3>
        <p className="text-base md:text-xl my-1 md:mb-2 leading-relaxed">
          Transformative Consulting for Today’s Systems into Tomorrow’s <br /> Solutions
          Crafting Digital Excellence for a Future Beyond Imagination
        </p>
        <p className="text-sm md:text-lg mb-0 md:mt-0 leading-relaxed mr-0">
          <span
            className={styles.earth}
            data-testid="earth-text"
            onMouseEnter={handleEarthHover}
          >
          </span>
        </p>
        <div className="w-full flex items-start">
          <a href="/our-capabilities">
            <motion.button
              className="relative inline-flex h-10 md:h-12 overflow-hidden rounded-md p-[3px] focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-slate-50 mb-2 md:mb-0"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#60a5fa_0%,#60a5fa_80%,#E2CBFF_10%)]" />
              <span className="inline-flex h-full w-full cursor-pointer items-center justify-center rounded-md bg-slate-950 px-3 py-1 text-sm font-medium text-white backdrop-blur-3xl">
                Start Exploring
              </span>
            </motion.button>
          </a>
        </div>
      </motion.div>
    </section>
  );
}